const React = require('react');
const { useSelector } = require('react-redux');
const { useUrlState } = require('../utils/context/url');
const { usePlatformState } = require('../utils/context/platform');
const { useChatDispatch, useChatState } = require('../utils/context/chatProvider');
const { loginUrl } = require('../utils');
const addQueryParam = require('../utils/addQueryParams');
const { CxWidget } = require('help-daisy');
const { useI18n } = require('../utils/context/i18nContext');
const useMetrics = require('./useMetrics');

const { useRef } = React;

const useContactRedirect = ({
  chat,
  flow,
  omniOneClickButton,
  contactData,
  desktopChatEnabled,
  isSocialNetwork,
  isNativeMobile,
  customSourceId,
  link,
  sendMelidataMelichatTrack,
  sendMelidataExperimentTrack,
  userSegment,
}) => {
  const i18n = useI18n();
  const hasCreditRestriction = useSelector((state) => state.hasCreditRestriction);
  const { homeUrl, baseUrl } = useUrlState();
  const { id: bu, siteId } = usePlatformState();
  const contactBtnRef = useRef(null);
  const closeWidgetLabel = i18n.gettext('Cerrar');
  const chatDispatch = useChatDispatch();
  const { chatReference } = useChatState();
  const chatFlow = `${chat}?flow=${flow}`;
  const { increment } = useMetrics();

  const sendMelidataContactTrack = (buttonType = 'STATIC') => {
    const eventData = {
      portal_content_id: contactData.context.faq_id || -1,
      portal_content_destination_url: '/ONECLICK/WIDGET',
      button_type: buttonType,
    };

    if (window && window.melidata) {
      window.melidata('cleanAndSend', 'event', {
        path: '/portal/action/click',
        event_data: eventData,
      });
    }
  };

  const isUserLogged = () => contactData.context.user_id !== -1;

  const sendUserToLogin = () => {
    const customUrl = `${isSocialNetwork ? '&isSocialNetwork=true' : ''}${customSourceId ? `&customSourceId=${customSourceId}` : ''}`;
    const callbackUrl = `${homeUrl}${baseUrl}?performContact=true${customUrl}`;
    window.location.href = loginUrl(bu, siteId, isNativeMobile, encodeURIComponent(callbackUrl));
  };

  const increscentSourceIdParam = (url, isFloatingButton) => {
    const source_id = bu === 'MP' ? '1972' : '2023';
    if (isFloatingButton) {
      if (!url.includes('sourceId')) {
        const queryParam = url.includes('?') ? '&' : '?';
        const result = `${url}${queryParam}sourceId=${source_id}`;
        return result;
      }
    }
    return url;
  };

  const getHelpChatUrl = () => {
    const helpChatUrl = `${homeUrl}${baseUrl}/chat/v2`;
    return helpChatUrl;
  };

  const getMetricTags = (tags = {}) => ({
    user_segment: userSegment || 'unknown',
    ...tags,
  });

  const nativeClick = (isFloatingButton) => {
    if (sendMelidataMelichatTrack) {
      sendMelidataMelichatTrack();
    }

    const mobileUrlParam = chatFlow.includes('url') ? 'url=' : 'uri=';

    const [deepLink] = chatFlow.split(mobileUrlParam);

    const helpChatUrl = getHelpChatUrl();
    const newUrl = increscentSourceIdParam(helpChatUrl, isFloatingButton);

    const updatedUrl = addQueryParam(newUrl, 'hasCreditRestriction', hasCreditRestriction);
    const newDeepLink = `${deepLink}${mobileUrlParam}${encodeURIComponent(updatedUrl)}`;

    increment('contact.redirect.native_click', 1, {
      tags: getMetricTags({ url: newDeepLink }),
    });
    window.location.assign(newDeepLink);
  };

  const desktopClick = (isFloatingButton) => {
    if (sendMelidataMelichatTrack) {
      sendMelidataMelichatTrack();
    }
    window.name = 'homePortalTab';
    if (chatReference?.document?.location?.pathname.includes('/ayuda/chat')
      || chatReference?.document?.location?.pathname.includes('/ajuda/chat')
    ) {
      chatReference.window.focus();
    } else {
      const helpChatUrl = getHelpChatUrl();
      const newUrl = increscentSourceIdParam(helpChatUrl, isFloatingButton);
      const updatedUrl = addQueryParam((link || newUrl), 'hasCreditRestriction', hasCreditRestriction);
      const newChat = window.open(updatedUrl, 'chatTab');
      newChat.name = 'chatTab';
      chatDispatch({ type: 'setChatReference', chatReference: newChat });

      increment('contact.redirect.desktop_click', 1, {
        tags: getMetricTags({ url: updatedUrl }),
      });
    }
  };

  const handleClick = async (event, isFloatingButton) => {
    event.preventDefault();
    if (sendMelidataExperimentTrack) {
      sendMelidataExperimentTrack();
    }

    if (desktopChatEnabled) {
      desktopClick(isFloatingButton);
      return;
    }

    sendMelidataContactTrack(isFloatingButton ? 'FLOATING' : 'STATIC');

    if ((!omniOneClickButton && contactData.deepLink?.includes('melichat'))
      || (omniOneClickButton && contactData.omniDeeplink?.includes('melichat'))) {
      nativeClick(isFloatingButton);
      return;
    }

    if (isUserLogged()) {
      if (isNativeMobile && ((contactData.deepLink && !omniOneClickButton) || (contactData.omniDeeplink && omniOneClickButton))) {
        const deeplink = omniOneClickButton ? contactData.omniDeeplink : contactData.deepLink;
        const temp = increscentSourceIdParam(deeplink, isFloatingButton);
        increment('contact.redirect.native_mobile', 1, {
          tags: getMetricTags({ url: temp }),
        });
        window.location.href = temp;
      } else if (!contactData.onePContact) {
        CxWidget(contactData.context, { closeWidgetLabel }, contactBtnRef.current);
      } else {
        increment('contact.redirect.one_p_contact', 1, {
          tags: getMetricTags({ url: contactData.href }),
        });
        window.location.href = contactData.href;
      }
    } else {
      increment('contact.redirect.login', 1, {
        tags: getMetricTags({ url: 'loginUrl' }), // User is not logged in
      });
      sendUserToLogin();
    }
  };

  const propsContactInProgress = {
    onClick: (e) => handleClick(e, true),
    isMobile: isNativeMobile,
    bu,
  };

  return {propsContactInProgress, handleClick};
};

module.exports = useContactRedirect;
